/**
 * One microphone or one speaker, as the browser lists it.
 *
 * `label` is empty until the browser has handed out a microphone at least once:
 * device names are personal enough that a page gets them only after it has been
 * trusted with the device itself. Naming the nameless ones is the screen's job,
 * where the words live.
 */
export interface AudioDeviceOption {
  id: string;
  label: string;
}

export interface AudioDevices {
  microphones: AudioDeviceOption[];
  speakers: AudioDeviceOption[];
}

const MICROPHONE_KEY = 'practice:microphone';
const SPEAKER_KEY = 'practice:speaker';

/*
 * Storage is not always there — a locked-down browser throws on read as well as
 * on write — and a remembered microphone is a convenience, never a requirement.
 */
function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* Nothing is remembered; every other visit still works. */
  }
}

export function storedMicrophoneId(): string | null {
  return read(MICROPHONE_KEY);
}

export function storedSpeakerId(): string | null {
  return read(SPEAKER_KEY);
}

/**
 * Remember what was chosen, so the next conversation starts on the headset
 * rather than on the lid microphone it was moved off last time.
 */
export function rememberMicrophoneId(deviceId: string): void {
  write(MICROPHONE_KEY, deviceId);
}

export function rememberSpeakerId(deviceId: string): void {
  write(SPEAKER_KEY, deviceId);
}

/**
 * Whether a whole AudioContext can be sent to a chosen speaker.
 *
 * A practice call plays through the audio graph rather than through an
 * element, and Firefox can switch the one but not the other.
 */
export function contextSpeakerChoiceSupported(): boolean {
  return typeof AudioContext !== 'undefined' && 'setSinkId' in AudioContext.prototype;
}

function toOptions(devices: MediaDeviceInfo[]): AudioDeviceOption[] {
  return devices.map((device) => ({
    id: device.deviceId,
    label: device.label,
  }));
}

/**
 * What is plugged in, asked before there is a call to ask it of.
 *
 * Names come with it only once the browser has granted a microphone at least
 * once, which is why the preview stream is opened first. Speakers are listed
 * only where the call could actually play through the one chosen.
 */
export async function listAudioDevices(): Promise<AudioDevices> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  const ofKind = (kind: MediaDeviceKind) =>
    devices.filter((device) => device.kind === kind);

  return {
    microphones: toOptions(ofKind('audioinput')),
    speakers: contextSpeakerChoiceSupported() ? toOptions(ofKind('audiooutput')) : [],
  };
}

/**
 * The device to actually use, given what was remembered and what is there.
 *
 * A remembered device that has since been unplugged is not a choice any more,
 * and starting a conversation on a microphone that is gone is silence — so the
 * first one the browser lists takes over.
 */
export function resolveDeviceId(
  remembered: string | null,
  available: AudioDeviceOption[],
): string | null {
  if (remembered !== null && available.some((device) => device.id === remembered)) {
    return remembered;
  }

  return available[0]?.id ?? null;
}
